import { QueryClient } from '@tanstack/react-query';
import { createContext } from 'react';
import { type StoreApi } from 'zustand';

import { createAppStateStore, type AppStateStore } from './app-state-store.js';

export const queryClientConfig = {
  defaultOptions: {
    queries: {
      retry: false,
      // Data only changes via explicit mutations — prevent unnecessary refetches.
      staleTime: Infinity,
    },
    mutations: {
      retry: false,
    },
  },
};

/**
 * @internal
 */
export interface AppStateContextValue {
  store: StoreApi<AppStateStore>;
  scope: 'user' | 'app';
  queryClient: QueryClient;
}

// Default store used when no AppStateProvider is present.
// isLoading is false — no provider means no bulk fetch will happen.
const defaultStore = createAppStateStore();
defaultStore.setState({ isLoading: false });

/**
 * Resets the default store's per-key tracking. Call in test `beforeEach` to
 * prevent state pollution across tests that use the no-provider code path.
 * @internal
 */
export function _resetDefaultStore(): void {
  defaultStore.setState({
    data: {},
    isLoading: false,
    error: null,
    fetchedKeys: new Set(),
    keyErrors: {},
    deletingKeys: new Set(),
  });
}

/**
 * QueryClient used by the default context (no AppStateProvider). Exported so
 * test helpers can call `.clear()` between tests to avoid stale-cache pollution.
 * @internal
 */
export const _defaultQueryClient = new QueryClient(queryClientConfig);

/**
 * @internal
 */
export const AppStateContext = createContext<AppStateContextValue>({
  store: defaultStore,
  scope: 'user',
  queryClient: _defaultQueryClient,
});
